import {Injectable, NgZone, signal} from '@angular/core';
import {NavigationEnd, Router} from '@angular/router';
import {filter} from 'rxjs';
import {environment} from '../environments/environment';
import {AuthService} from './auth-service';

const DEFAULT_IDLE_TIMEOUT_MINUTES = 15;
const DEFAULT_WARNING_SECONDS = 120;
const CHECK_INTERVAL_MS = 1000;
const ACTIVITY_WRITE_THROTTLE_MS = 5000;
const LAST_ACTIVITY_KEY = 'pxcat_last_activity';
// 'click' also covers programmatic clicks and Angular (click) handlers; listeners use the capture phase
// so they fire even when a handler calls stopPropagation()
const ACTIVITY_EVENTS = ['mousedown', 'click', 'keydown', 'scroll', 'wheel'];

/**
 * Terminates the session after a period of user inactivity (H.9 Session Termination).
 * Last activity is shared across tabs via localStorage, so activity in any tab keeps the session alive.
 * oidc-client-ts silent renew would otherwise keep an idle session alive for the full refresh token lifetime.
 */
@Injectable({
  providedIn: 'root'
})
export class IdleTimeoutService {
  private readonly idleTimeoutMs = (environment.session?.idleTimeoutMinutes ?? DEFAULT_IDLE_TIMEOUT_MINUTES) * 60_000;
  private readonly warningMs = (environment.session?.warningSeconds ?? DEFAULT_WARNING_SECONDS) * 1000;
  private intervalId: ReturnType<typeof setInterval> | null = null;
  private lastActivityWrite = 0;
  private readonly onActivity = () => this.recordActivity();

  public readonly secondsRemaining = signal<number | null>(null);

  constructor(private readonly authService: AuthService, private readonly zone: NgZone, private readonly router: Router) {
    this.authService.user$.subscribe(user => user ? this.start() : this.stop());
    // Angular route changes (routerLink, router.navigate, back/forward) count as activity
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        if (this.intervalId) {
          this.recordActivity();
        }
      });
  }

  public stayLoggedIn(): void {
    this.recordActivity(true);
  }

  private start(): void {
    if (this.intervalId) {
      return;
    }
    // A session left idle past the timeout (e.g. tab closed and reopened later) is terminated on load
    const stored = Number(sessionStorage.getItem(LAST_ACTIVITY_KEY));
    if (stored && Date.now() - stored >= this.idleTimeoutMs) {
      this.terminateSession();
      return;
    }
    console.info(`Session idle timeout active: ${this.idleTimeoutMs / 60_000} min`);
    this.recordActivity(true);
    this.zone.runOutsideAngular(() => {
      ACTIVITY_EVENTS.forEach(event => window.addEventListener(event, this.onActivity, {passive: true, capture: true}));
      this.intervalId = setInterval(() => this.check(), CHECK_INTERVAL_MS);
    });
  }

  private stop(): void {
    ACTIVITY_EVENTS.forEach(event => window.removeEventListener(event, this.onActivity, {capture: true}));
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    this.secondsRemaining.set(null);
  }

  private recordActivity(force = false): void {
    // While the warning is shown, any activity dismisses it immediately (no throttling)
    const now = Date.now();
    if (force || this.secondsRemaining() !== null || now - this.lastActivityWrite > ACTIVITY_WRITE_THROTTLE_MS) {
      this.lastActivityWrite = now;
      sessionStorage.setItem(LAST_ACTIVITY_KEY, String(now));
      this.secondsRemaining.set(null);
    }
  }

  private lastActivity(): number {
    return Number(sessionStorage.getItem(LAST_ACTIVITY_KEY)) || Date.now();
  }

  private check(): void {
    const remainingMs = this.idleTimeoutMs - (Date.now() - this.lastActivity());
    if (remainingMs <= 0) {
      this.terminateSession();
    } else if (remainingMs <= this.warningMs) {
      this.secondsRemaining.set(Math.ceil(remainingMs / 1000));
    } else if (this.secondsRemaining() !== null) {
      // Activity in another tab extended the session
      this.secondsRemaining.set(null);
    }
  }

  private terminateSession(): void {
    console.warn('Session terminated due to inactivity');
    this.stop();
    sessionStorage.removeItem(LAST_ACTIVITY_KEY);
    this.authService.logout().then();
  }
}
